import React, { useState, useLayoutEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { t } from "i18next";
import { useParams } from "react-router-dom";
import { WithTransparentSpinner } from "components/Hoc/WithTransparentSpinner";
import { getPaymentLinkRedirectionObj, validateOutletOrderObject, logPopupError, getOutletOrderObject } from "utils/Utils";
import C from "Constants";
import { setAFSWplOptions, getAmountToBePaid, toBase64 } from "../helpers/utils";
import AfsWidgetLoader from "./widget-loader";
import AFS_API from "../helpers/api";
import "../styles/index.css";

const AFSPaymentWidget = ({ paymentLinkInfo = {},
  // successPath = "",
} = {}) => {

  const isDineIn = window?.location?.pathname.includes("/dine-in");
  const params = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [afsPaymentData, setAfsPaymentData] = useState();
  const isMountedRef = useRef(true);

  const { selectedStore } = useSelector((state) => state.store);
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  const { DomainType } = useSelector((state) => state.AppReducer);
  const { orderGrossAmount, totalOrdersGrossAmount, isSplitted, split } =
    useSelector((state) => state?.cart);
  const { ordered } = useSelector((state) => state.order);

  const prepareAFSCheckout = async () => {
    if (!isMountedRef.current) return;
    try {

      const amountTobePaid = getAmountToBePaid({
        paymentLinkInfo,
        isSplitted,
        ordered,
        totalOrdersGrossAmount,
        orderGrossAmount,
        split,
      });

      // orderMetaData will only be saved for order placing
      let orderMetaData = {};
      if (!paymentLinkInfo || Object.keys(paymentLinkInfo).length <= 0) {
        const { orderObject, isValid, errors } = validateOutletOrderObject();
        if (!isValid) {
          logPopupError(errors, true);
          return;
        }
        orderMetaData = getOutletOrderObject(orderObject);
      }

      const payload = {
        amount: amountTobePaid?.toFixed(2),
        currency: "AED",
        storeId: paymentLinkInfo?.storeId || selectedStore?._id,
        brandId: paymentLinkInfo?.brandId || selectedStore?.brandId || brandInfo?.brand?._id,
        domainType: DomainType,
        paymentType: "DB",
        ...(paymentLinkInfo?.cartId ? { cartId: paymentLinkInfo?.cartId || "", mode: "payment_link" } : {}),
        orderMetaData: orderMetaData,
      };

      const signedPayload = await toBase64(JSON.stringify(payload));

      const response = await AFS_API.prepareAFSCheckout({
        signedUrl: signedPayload,
      });
      
      if (!response?.data?.id) throw new Error("Missing checkoutId");

      if (!isMountedRef.current) return;

      setAFSWplOptions({ ...response?.data, amount: amountTobePaid }).then((afsPaymentData) => {
        if (isMountedRef.current) {
          setAfsPaymentData(response?.data);
        }
      }).catch((error) => {
        setIsLoading(false);
        if (isMountedRef.current) {
          throw new Error(error?.message);
        }
      });

    } catch (error) {
      setIsLoading(false);
      if (isMountedRef.current) {
        console.error(error);
        logPopupError(error?.message, true);
      }
    }
  };

  const getNonPaymentLinkSuccessPath = () => {

    const origin = window.location.origin;
    const search = new URLSearchParams(window.location.search);
    search.set("checkoutId", afsPaymentData?.id);
    const successPathUrl = isDineIn
      ? DomainType === C.TEMPORARAY_DOMAIN
        ? `/${params.domainName}/dine-in/payment-confirm`
        : `/dine-in/payment-confirm`
      : DomainType === C.TEMPORARAY_DOMAIN
        ? `/${params.domainName}/payment-confirm`
        : "/payment-confirm";
    return `${origin}${successPathUrl}?${search.toString()}`;
  }

  const successPath = useMemo(() => {
    let path = "";
    if (paymentLinkInfo?.amount) {
      const obj = {
        token: null,
        cartId: paymentLinkInfo?.cartId,
        brandId: paymentLinkInfo?.brandId || selectedStore?.brandId,
        DomainType: DomainType,
        domainName: params?.domainName,
      };
      path = getPaymentLinkRedirectionObj(obj)?.successUrl;
      path = path?.replace?.(
        "paymentStatus=success",
        `checkoutId=${afsPaymentData?.id}`
      );
    } else {
      path = getNonPaymentLinkSuccessPath();
    }

    return path;
  }, [afsPaymentData?.id]);

  useLayoutEffect(() => {
    localStorage.setItem("activePaymentMethod", "AFS_PAYMENT");
    isMountedRef.current = true;
    prepareAFSCheckout();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
        <WithTransparentSpinner loading={isLoading} />
        <div id="afs-container">
          {!isLoading && !afsPaymentData?.id && (
            <div
              style={{ height: "450px" }}
              className="h-100 border border-gray-200 rounded-md bg-gray-100 p-4 flex justify-center items-center text-center text-gray-500"
            >
              {t("Something went wrong. Please try again later.")}
            </div>
          )}
        </div>
        {afsPaymentData?.id && afsPaymentData?.integrity && (
          <AfsWidgetLoader
            afsPaymentData={afsPaymentData}
            successPath={successPath}
            setIsLoading={setIsLoading}
          />
        )}
    </>
  );
};

export default AFSPaymentWidget;
